import {
  AlertTriangle,
  BellRing,
  CheckCircle2,
  Clock,
  Mail,
  Search,
  Trash2,
  Trophy,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LeadCard } from "@/components/admin/LeadCard";
import {
  SERVICE_OPTIONS,
  SummaryTile,
  TONE_CLASSES,
} from "@/components/admin/leadBoardTokens";
import { money } from "@/lib/format";

/**
 * The furniture around the follow-up board: its heading and filters, the KPI
 * tiles, the "email is off" warning, the empty state and a single column.
 *
 * All presentational and prop-driven, so the board component itself is left
 * holding only the things that are genuinely stateful - loading, dragging,
 * which lead is open - instead of four hundred lines of layout.
 */

export const BoardHeader = ({ quietDays, query, onQuery, service, onService }) => (
  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
    <div>
      <h3 className="text-base font-bold text-foreground">Follow-up board</h3>
      <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        Every quoted lead, in the stage it&apos;s actually at. Change a stage with the dropdown
        on the card, or drag it between columns. Open a card to leave a note, set the next
        action or download the quote. Anything open with no activity for {quietDays} days is
        flagged as gone quiet.
      </p>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          data-testid="lead-board-search-input"
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          placeholder="Name, email, company, reference…"
          className="h-10 w-full pl-9 sm:w-[240px]"
        />
      </div>
      <Select value={service} onValueChange={onService}>
        <SelectTrigger className="h-10 w-[180px]" data-testid="lead-board-service-filter">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SERVICE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  </div>
);

export const BoardSummary = ({ summary, quietDays, loading }) => {
  const figure = (value) => (loading ? "\u2014" : value);

  return (
    <div
      className={`mt-5 grid grid-cols-2 gap-3 ${
        summary.false_leads ? "xl:grid-cols-5" : "xl:grid-cols-4"
      }`}
    >
      <SummaryTile
        testId="lead-board-open-tile"
        icon={Clock}
        label="Open leads"
        value={figure(summary.open ?? 0)}
        hint="Sitting in New, Contacted or Awaiting decision"
      />
      <SummaryTile
        testId="lead-board-quiet-tile"
        icon={BellRing}
        tone="amber"
        label="Gone quiet"
        value={figure(summary.gone_quiet ?? 0)}
        hint={`No activity for ${quietDays}+ days`}
      />
      <SummaryTile
        testId="lead-board-overdue-tile"
        icon={AlertTriangle}
        tone="amber"
        label="Action overdue"
        value={figure(summary.action_overdue ?? 0)}
        hint="Next action date has already passed"
      />
      <SummaryTile
        testId="lead-board-won-tile"
        icon={Trophy}
        tone="success"
        label="Won value"
        value={figure(money(summary.won_value ?? 0))}
        hint="Total quoted value marked as won"
      />
      {/* Only shown once there is something to show: an always-visible
          "0 false leads" tile is just noise on the busiest screen. */}
      {summary.false_leads ? (
        <SummaryTile
          testId="lead-board-false-tile"
          icon={Trash2}
          tone="muted"
          label="False leads"
          value={figure(summary.false_leads)}
          hint="Set aside, and kept out of these numbers"
        />
      ) : null}
    </div>
  );
};

export const EmailOffNotice = ({ message }) => (
  <p
    data-testid="lead-board-email-off-notice"
    className="mt-4 flex items-start gap-2 rounded-lg border px-4 py-3 text-[13px] leading-relaxed"
    style={{
      borderColor: "var(--tt-warn-border)",
      backgroundColor: "var(--tt-warn-bg)",
      color: "var(--tt-warn-text)",
    }}
  >
    <Mail className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
    <span>
      <strong className="font-bold">Quote emails are off.</strong> {message} Add{" "}
      <span className="font-amount">RESEND_API_KEY</span> and{" "}
      <span className="font-amount">SENDER_EMAIL</span> to the backend environment and restart
      the backend to switch them on.
    </span>
  </p>
);

export const BoardEmpty = ({ filtered }) => (
  <div
    data-testid="lead-board-empty"
    className="mt-5 rounded-xl border border-dashed border-border py-16 text-center"
  >
    <CheckCircle2 className="mx-auto h-8 w-8 text-muted-foreground/50" aria-hidden="true" />
    <p className="mt-3 text-sm font-semibold text-foreground">No leads to follow up</p>
    <p className="mt-1 text-sm text-muted-foreground">
      {filtered
        ? "Nothing matches those filters."
        : "As soon as someone completes the contact gate their quote lands here."}
    </p>
  </div>
);

export const BoardColumn = ({ column, stages, dragRef, isDropTarget, onDragOver,
                              onDragLeave, onDrop, onOpen, onStageChange, onDragStart }) => (
  <section
    data-testid={`lead-column-${column.key}`}
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    className={`flex flex-col rounded-xl border bg-[color:var(--tt-surface-2)] transition-[border-color,background-color] duration-150 ${
      isDropTarget ? "border-primary bg-[color:var(--tt-teal-mist)]" : "border-border"
    }`}
  >
    <header className="border-b border-border px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-[13px] font-bold text-foreground">{column.label}</h4>
        <Badge
          variant="outline"
          data-testid={`lead-column-count-${column.key}`}
          className={`px-1.5 py-0 font-amount text-[11px] font-bold ${
            TONE_CLASSES[column.tone] || TONE_CLASSES.muted
          }`}
        >
          {column.count}
        </Badge>
      </div>
      <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{column.blurb}</p>
      {column.gone_quiet ? (
        <p className="mt-1 text-[10px] font-semibold text-[color:var(--tt-warn-text)]">
          {column.gone_quiet} gone quiet
        </p>
      ) : null}
    </header>

    <div className="flex-1 space-y-2.5 p-2.5">
      {column.cards.length === 0 ? (
        <p className="py-6 text-center text-[11px] text-muted-foreground">Nothing here</p>
      ) : (
        column.cards.map((card) => (
          <LeadCard
            key={card.quote_ref}
            card={card}
            stages={stages}
            dragging={dragRef === card.quote_ref}
            onOpen={onOpen}
            onStageChange={onStageChange}
            onDragStart={onDragStart}
          />
        ))
      )}
      {column.count > column.shown ? (
        <p className="pt-1 text-center text-[10px] text-muted-foreground">
          showing the {column.shown} most recently active of {column.count}
        </p>
      ) : null}
    </div>
  </section>
);
